import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { BuyerSupportChatQuery } from 'interfaceAdapter/queries/BuyerSupportChat';
import { BuyerSupportChatRepository } from 'interfaceAdapter/repositories/BuyerSupportChat';
import { useLoading } from 'application/contexts/useLoading';
import { IChat } from 'domain/entities/Chat/Chat';

type BuyerSupportChatContext = {
    loading: boolean;
    chatMessages: IChat[];
    message: string;
    setMessage: (message: string) => void;
    sendMessage: () => void;
    fetchData: () => void;
};

const defaultContext: BuyerSupportChatContext = {
    loading: false,
    chatMessages: [],
    message: '',
    setMessage: () => {},
    sendMessage: () => {},
    fetchData: () => {},
};

export const buyerSupportChatContext = createContext<BuyerSupportChatContext>(defaultContext);

export const useBuyerSupportChat = (): BuyerSupportChatContext => {
    const diContainer = useContext(diContainerContext);
    const buyerSupportChatQuery = diContainer.resolve(BuyerSupportChatQuery);
    const buyerSupportChatRepository = diContainer.resolve(BuyerSupportChatRepository);
    const { loading, setLoading } = useLoading();
    const [chatMessages, setChatMessages] = useState<IChat[]>([]);
    const [message, setMessage] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await buyerSupportChatQuery.fetchMessages();
            setChatMessages(data);
        } catch (error) {
            console.error('サポートチャットメッセージの取得に失敗しました:', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // チャットメッセージ送信
    const sendMessage = useCallback(async () => {
        if (!message.trim()) return;
        setLoading(true);
        try {
            await buyerSupportChatRepository.send(message);
            setMessage('');
            await fetchData(); // メッセージ一覧を更新
        } catch (error) {
            console.error('メッセージ送信エラー:', error);
        } finally {
            setLoading(false);
        }
    }, [message, buyerSupportChatRepository, fetchData, setLoading]);

    return {
        loading,
        chatMessages,
        message,
        setMessage,
        sendMessage,
        fetchData,
    };
};

